import eachElement from 'Utils/eachElement.js';
import errorHandler from 'Utils/errorHandler.js';
import whenAvailable from 'Utils/whenAvailable.js';

const interactionTrackedElements = new WeakSet();
const inlineObservers = new WeakMap();
const popupHandlers = new WeakMap();

/* Ensure the element is empty */
const emptyElement = element => {
	while (element.firstElementChild) {
		element.firstElementChild.remove();
	}
};

/* Check if an element is visible */
const isVisible = element => {
	while (element) {
		const style = window.getComputedStyle(element);
		if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
			return false;
		}

		element = element.parentElement;
	}

	return true;
};

/* Track whether the user has interacted with Calendly in any way */
const trackInteraction = element => {
	if (interactionTrackedElements.has(element)) {
		return;
	}

	const widgetType = getWidgetType(element);

	/* Slightly different event types, as we don't want to track hovering on top of a popup trigger */
	const events = widgetType === 'inline' ? ['click', 'touch', 'mouseover', 'touchmove'] : ['click', 'touch', 'mousedown'];

	/* Create a listener for all appropriate events */
	for (const event of events) {
		element.addEventListener(event, () => {
			window.hasInteractedWithCalendly = true;
		});
	}

	interactionTrackedElements.add(element);
};

/* Event handler for redirecting to Calendly */
const redirectToCalendly = event => {
	const url = getCalendlyURL(event.target);

	if (url) {
		location.assign(url);
	}
};

/* Get the type of widget ("inline" or "popup") */
const getWidgetType = element => String(element.dataset.widgetType).toLowerCase() === 'inline' ? 'inline' : 'popup';

/* Get the Gearset Calendly account name */
const getGearsetAccount = element => element.dataset.calendlyAccount ? String(element.dataset.calendlyAccount) : 'gearset';

/* Construct the Calendly calendar URL based on element attributes */
const getCalendlyURL = element => {
	const account = getGearsetAccount(element);
	const url = new URL(`/${account}/${element.dataset.calendly}?hide_gdpr_banner=1`, 'https://calendly.com/');

	if (element.dataset.hideInfo) {
		url.searchParams.append('hide_event_type_details', '1');
	}

	return url.toString();
};

/* Construct Calendly settings object */
const getCalendlySettings = element => {
	const widgetType = getWidgetType(element);

	/* Set URL */
	const settings = {
		url: getCalendlyURL(element),
	};

	/* Add Ruler, if available */
	if ('RulerAnalyticsVisitorId' in window) {
		settings.utm = {
			utmContent: window.RulerAnalyticsVisitorId,
		};
	}

	/* Add inline specific settings, if appropriate */
	if (widgetType === 'inline') {
		settings.parentElement = element;
		settings.prefill = {};
		settings.resize = element.dataset.autoResize === 'true';
	}

	return settings;
};

/* Load CSS file for pop-in calendar, if necessary */
const loadCalendlyCss = (() => {
	let isCssLoaded = false;

	return () => {
		if (isCssLoaded) {
			return;
		}

		const css = document.createElement('link');
		css.rel = 'stylesheet';
		css.href = 'https://assets.calendly.com/assets/external/widget.css';
		document.head.append(css);
		isCssLoaded = true;
	};
})();

const removeInlineObserver = element => {
	const observer = inlineObservers.get(element);

	if (observer) {
		observer.disconnect();
		inlineObservers.delete(element);
	}
};

const removePopupHandler = element => {
	const handleClick = popupHandlers.get(element);

	if (handleClick) {
		element.removeEventListener('click', handleClick);
		popupHandlers.delete(element);
	}
};

/* Initialise Calendly */
const initCalendly = element => {
	/* Prep the element */
	trackInteraction(element);
	element.removeEventListener('click', redirectToCalendly);

	/* Get Calendly settings */
	const settings = getCalendlySettings(element);
	const widgetType = getWidgetType(element);

	if (widgetType === 'inline' && element) {
		removePopupHandler(element);
		removeInlineObserver(element);

		/* Only initialise inline Calendly if it's visible */
		const observer = new IntersectionObserver((entries, observer) => {
			for (const entry of entries) {
				if (!(entry.isIntersecting && isVisible(entry.target))) {
					continue;
				}

				/* Clear any previous Calendly iframe */
				emptyElement(element);

				/* Set up inline Calendly (e.g. book a demo modal) */
				try {
					window.Calendly.initInlineWidget(settings);
				} catch (error) {
					errorHandler(error);
				}

				/* Stop observing */
				observer.unobserve(entry.target);
				inlineObservers.delete(element);
			}
		}, {
			threshold: 0.1,
		});

		inlineObservers.set(element, observer);
		observer.observe(element);
	} else {
		removeInlineObserver(element);
		removePopupHandler(element);

		/* Open Calendly pop-in calendar on trigger */
		const handleClick = event => {
			event.preventDefault();

			/* Get pop-in CSS */
			loadCalendlyCss();

			try {
				window.Calendly.initPopupWidget(settings);
			} catch (error) {
				/* Log the error */
				errorHandler(error);

				/* Send the user to Calendly as a fallback */
				redirectToCalendly(event);
			}
		};

		/* Set up button events (remove any existing one) */
		element.addEventListener('click', handleClick);
		popupHandlers.set(element, handleClick);
	}
};

/* Handle user communication and error logging if Calendly failed to load */
const handleFallback = element => {
	/* Get Calendly settings */
	const widgetType = getWidgetType(element);

	if (widgetType === 'inline' && element) {
		/* Clear any previous iframe */
		emptyElement(element);

		/* Communicate to user */
		element.innerHTML = '<div><p class="notification is-red my-6">Sorry, we\'re having some trouble loading our calendar right now. Please reach out to us through the chat in the bottom corner of the screen!</p></div>';

		/* Log the error */
		errorHandler(new Error('Calendly failed to load in time for inline scheduler'));
	} else {
		/* Send user to Calendly's hosted URL as a fallback */
		element.addEventListener('click', redirectToCalendly);
	}
};

/* Wire up a single Calendly trigger element */
const setUpCalendlyTrigger = element => {
	/* If it's not inline, set an initial click handler so clicks before Calendly loads still work */
	const widgetType = getWidgetType(element);
	if (widgetType !== 'inline') {
		element.addEventListener('click', redirectToCalendly);
	}

	/* Load Calendly initially */
	whenAvailable('Calendly')
		.then(() => initCalendly(element))
		.catch(() => handleFallback(element));

	/* Re-load once/if Ruler also becomes available */
	whenAvailable(['Calendly', 'RulerAnalyticsVisitorId'])
		.then(() => {
			if (!window.hasInteractedWithCalendly) {
				initCalendly(element);
			}
		})
		.catch(() => {
			if (!window.hasInteractedWithCalendly && !('Calendly' in window)) {
				handleFallback(element);
			}
		});
};

/* Open a Calendly popup from a ?calendly= query parameter */
const openCalendlyFromUrl = () => {
	const urlParameters = new URLSearchParams(window.location.search);
	const calendlyParameter = urlParameters.get('calendly');

	if (!calendlyParameter) {
		return;
	}

	/* Find a popup trigger for this event; multiple buttons may share the same slug */
	const trigger = [...document.querySelectorAll('[data-calendly]')].find(element => (
		element.dataset.calendly === calendlyParameter && getWidgetType(element) !== 'inline'
	));

	if (!trigger) {
		errorHandler(new Error(`Tried to open non-existent Calendly event: ${calendlyParameter}`));
		return;
	}

	/* Remove the calendly parameter from the URL */
	const url = new URL(window.location);
	url.searchParams.delete('calendly');
	window.history.replaceState({}, '', url);

	whenAvailable('Calendly')
		.then(() => {
			window.hasInteractedWithCalendly = true;
			loadCalendlyCss();

			try {
				window.Calendly.initPopupWidget(getCalendlySettings(trigger));
			} catch (error) {
				errorHandler(error);
				location.assign(getCalendlyURL(trigger));
			}
		})
		.catch(() => {
			/* Send the user to Calendly as a fallback */
			location.assign(getCalendlyURL(trigger));
		});
};

/* Initialise Calendly on the page */
const setUpCalendly = () => {
	/* Whether the user has already interacted with the Calendly iframe or not */
	window.hasInteractedWithCalendly = false;

	/* For each Calendly trigger on the page */
	eachElement('[data-calendly]', setUpCalendlyTrigger);

	/* Open Calendly popups from the URL */
	openCalendlyFromUrl();
};

export default {
	setUpCalendly,
	setUpCalendlyTrigger,
};
